import React from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { Award, Briefcase, CheckCircle, Clock, DollarSign, ExternalLink, FileText, Landmark, Phone, PieChart, Search, Settings, Shield, Star, Wallet } from 'lucide-react';

// Extracted from WorkerDashboard.js — receives everything it needs via `ctx`.
export default function WorkerProfileTab({ ctx }) {
  const { activeTab, earnings, getProfilePhotoUrl, label, setActiveTab, setShowCertificates, setShowEarnings, setShowProfileSettings, setShowWalletManagement, user } = ctx;
  return (
    <>
    {activeTab === 'profile' && (
      <div className="space-y-6">
        {/* Background Verification card */}
        {user?.checkr_assessment === 'clear' ? (
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-5 text-white flex items-center gap-4" data-testid="worker-bg-verified-card">
            <div className="p-3 bg-white/20 rounded-xl"><Shield className="w-6 h-6" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-lg flex items-center gap-2">Background Verified <CheckCircle className="w-5 h-5" /></p>
              <p className="text-sm text-white/85">You passed your background check — employers see a verified badge on your applications, boosting your chances of getting picked.</p>
            </div>
          </div>
        ) : (user?.checkr_status === 'invited' || user?.checkr_status === 'pending') ? (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 flex items-center gap-4" data-testid="worker-bg-pending-card">
            <div className="p-3 bg-blue-100 rounded-xl"><Clock className="w-6 h-6 text-blue-600" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-blue-900">Background check in progress</p>
              <p className="text-sm text-blue-700">
                {user?.checkr_status === 'invited'
                  ? 'Check your email for the Checkr invitation and complete it to get verified.'
                  : 'Checkr is processing your report — you\u2019ll be notified when it completes.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white border-2 border-dashed border-indigo-200 rounded-2xl p-5 flex flex-wrap items-center gap-4" data-testid="worker-get-verified-card">
            <div className="p-3 bg-indigo-50 rounded-xl"><Shield className="w-6 h-6 text-indigo-600" /></div>
            <div className="flex-1 min-w-[220px]">
              <p className="font-bold text-slate-900">Get Background Verified</p>
              <p className="text-sm text-slate-500">Verified workers stand out — employers see a trust badge on your shift applications and pick verified workers more often.</p>
            </div>
            <button
              onClick={async () => {
                try {
                  const r = await apiCall('/api/checkr/request-verification', { method: 'POST' });
                  toast.success(r.message || 'Request sent');
                } catch (e) {
                  toast.error(e.message || 'Request failed');
                }
              }}
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition"
              data-testid="get-verified-btn"
            >
              Get Verified
            </button>
          </div>
        )}
        {/* Profile Completeness Section */}
        {(() => {
          const checks = [
            { label: 'Full Name', done: !!user?.full_name },
            { label: 'Phone Number', done: !!user?.phone },
            { label: 'Profile Photo', done: !!user?.profile_photo },
            { label: 'Bio / About Me', done: !!user?.bio },
            { label: 'Address', done: !!user?.address },
            { label: 'Skills', done: user?.skills?.length > 0 },
            { label: 'Certifications', done: user?.certifications?.length > 0 },
            { label: 'Desired Rate', done: !!user?.hourly_rate },
          ];
          const completePct = Math.round((checks.filter(c => c.done).length / checks.length) * 100);
          return (
            <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="profile-completeness">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-800">Profile Completeness</h3>
                <span className="text-2xl font-bold text-slate-800">{completePct}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-5">
                <div className="h-3 rounded-full transition-all duration-500" style={{width: `${completePct}%`, background: completePct === 100 ? '#10b981' : '#3b82f6'}} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {checks.map((c) => (
                  <div key={c.label} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${c.done ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-500'}`}>
                    {c.done ? <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />}
                    <span className="font-medium">{c.label}</span>
                  </div>
                ))}
              </div>
              {completePct === 100 && (
                <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Award className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-800">Verified Profile Badge Earned!</p>
                    <p className="text-sm text-emerald-600">Your profile is complete and verified</p>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Digital Resume Card */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 text-white" data-testid="digital-resume-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/10 rounded-xl">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Digital Resume</h3>
                <p className="text-white/70 text-sm">Your shareable achievement profile</p>
              </div>
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(window.location.origin + '/resume/' + user?._id); toast.success('Resume link copied!'); }}
              className="px-5 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-medium transition flex items-center gap-2"
              data-testid="share-resume-link-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Share Link
            </button>
          </div>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 px-6 py-10">
            <div className="relative z-10 flex items-center gap-6">
              <div className="w-24 h-24 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border-4 border-white/30 overflow-hidden">
                {user?.profile_photo ? (
                  <img 
                    src={getProfilePhotoUrl(user.profile_photo)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-4xl font-bold text-white">
                    {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-1">{user?.full_name}</h3>
                <p className="text-white/80">{user?.email}</p>
                <div className="flex items-center gap-3 mt-3">
                  <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg text-sm font-medium capitalize">
                    {user?.role}
                  </span>
                  <span className="px-3 py-1.5 bg-emerald-500/30 rounded-lg text-sm font-medium flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Verified Profile
                  </span>
                </div>
              </div>
            </div>
            <div className="absolute -right-16 -top-16 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          </div>
          
          {/* Profile Details */}
          <div className="p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Full Name</label>
                <p className="text-base font-semibold text-gray-900">{user?.full_name}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Email Address</label>
                <p className="text-base font-semibold text-gray-900">{user?.email}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Phone Number</label>
                <p className="text-base font-semibold text-gray-900">{user?.phone || 'Not provided'}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Account Type</label>
                <p className="text-base font-semibold text-gray-900 capitalize">{user?.role}</p>
              </div>
              {user?.bio && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 md:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Bio</label>
                  <p className="text-base text-gray-800">{user.bio}</p>
                </div>
              )}
              {user?.address && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Address</label>
                  <p className="text-base font-semibold text-gray-900">{user.address}</p>
                </div>
              )}
              {user?.hourly_rate && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Desired Rate</label>
                  <p className="text-base font-semibold text-gray-900">${user.hourly_rate}/hr</p>
                </div>
              )}
            </div>
            
            <div className="pt-4 border-t border-gray-200 flex flex-wrap gap-3">
              <button 
                onClick={() => setShowProfileSettings(true)}
                className="px-5 py-2.5 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-700 transition flex items-center gap-2"
                data-testid="edit-profile-btn"
              >
                <Settings className="w-4 h-4" />
                Edit Profile
              </button>
              <button 
                onClick={() => {
                  setShowProfileSettings(true);
                  setTimeout(() => {
                    document.querySelector('[data-testid="settings-section-password"]')?.click();
                  }, 100);
                }}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
                data-testid="change-password-btn"
              >
                Change Password
              </button>
            </div>
          </div>
        </div>

        {/* Earned Badges / Certifications */}
        {user?.certifications?.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="earned-badges">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Earned Badges</h3>
              <button
                onClick={() => setShowCertificates(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition text-sm font-medium"
                data-testid="manage-certificates-btn"
              >
                <Award className="w-4 h-4" />
                Manage Certificates
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {user.certifications.map((cert, idx) => (
                <div key={`${cert}-${idx}`} className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 bg-amber-100 rounded-full flex items-center justify-center">
                    <Award className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">{cert}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skills */}
        {user?.skills?.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="skills-section">
            <h3 className="text-lg font-bold text-slate-800 mb-4">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {user.skills.map((skill, idx) => (
                <span key={`${skill}-${idx}`} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-4" data-testid="profile-stats">
          <div className="bg-white rounded-xl p-5 border border-gray-200 text-center">
            <div className="p-3 bg-blue-100 rounded-xl w-fit mx-auto mb-3">
              <Briefcase className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-slate-800">{earnings?.lifetime_shifts ?? earnings?.shifts_completed ?? 0}</p>
            <p className="text-sm text-gray-500 mt-1">Total Shifts</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-200 text-center">
            <div className="p-3 bg-emerald-100 rounded-xl w-fit mx-auto mb-3">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-3xl font-bold text-slate-800">${(earnings?.lifetime_earned ?? earnings?.total_earned)?.toFixed(0) || '0'}</p>
            <p className="text-sm text-gray-500 mt-1">Total Earned</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-200 text-center">
            <div className="p-3 bg-amber-100 rounded-xl w-fit mx-auto mb-3">
              <Star className="w-6 h-6 text-amber-600" />
            </div>
            <p className="text-3xl font-bold text-slate-800">{user?.rating?.toFixed(1) || '4.0'}</p>
            <p className="text-sm text-gray-500 mt-1">Average Rating</p>
          </div>
        </div>

        {/* Skill Badges */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="skill-badges">
          <h3 className="text-lg font-bold text-slate-800 mb-4">Achievement Badges</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: 'First Shift', icon: '1', color: 'blue', earned: (earnings?.lifetime_shifts ?? earnings?.shifts_completed ?? 0) >= 1 },
              { name: 'Certified Pro', icon: 'C', color: 'emerald', earned: (user?.certifications?.length || 0) >= 1 },
              { name: 'Multi-Certified', icon: 'M', color: 'purple', earned: (user?.certifications?.length || 0) >= 3 },
              { name: 'Team Player', icon: 'T', color: 'amber', earned: (earnings?.lifetime_shifts ?? earnings?.shifts_completed ?? 0) >= 5 },
            ].map((badge, idx) => (
              <div key={idx} className={`rounded-xl p-4 text-center border ${badge.earned ? `bg-${badge.color}-50 border-${badge.color}-200` : 'bg-gray-50 border-gray-200 opacity-50'}`}>
                <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center text-lg font-bold ${badge.earned ? `bg-${badge.color}-100 text-${badge.color}-600` : 'bg-gray-200 text-gray-400'}`}>
                  {badge.icon}
                </div>
                <p className={`text-sm font-semibold ${badge.earned ? 'text-slate-800' : 'text-gray-400'}`}>{badge.name}</p>
                <p className="text-xs text-gray-500 mt-1">{badge.earned ? 'Earned' : 'Locked'}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => setShowWalletManagement(true)}
            className="flex items-center gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:shadow-md hover:border-emerald-300 transition text-left"
            data-testid="wallet-management-btn"
          >
            <div className="p-3 bg-emerald-100 rounded-xl">
              <Landmark className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Wallet & Banking</h3>
              <p className="text-xs text-gray-500">Manage bank accounts</p>
            </div>
          </button>
          <button
            onClick={() => setShowEarnings(true)}
            className="flex items-center gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:shadow-md hover:border-green-300 transition text-left"
            data-testid="earnings-dashboard-btn"
          >
            <div className="p-3 bg-green-100 rounded-xl">
              <PieChart className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Earnings Dashboard</h3>
              <p className="text-xs text-gray-500">Track income</p>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className="flex items-center gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:shadow-md hover:border-blue-300 transition text-left"
            data-testid="job-center-btn"
          >
            <div className="p-3 bg-blue-100 rounded-xl">
              <Search className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Job Center</h3>
              <p className="text-xs text-gray-500">Find available shifts</p>
            </div>
          </button>
        </div>
      </div>
    )}
    </>
  );
}
